import { Button, Tooltip, useDisclosure } from '@chakra-ui/react'
import { generatePath, useNavigate } from 'react-router'
import { Paths } from 'src/constants/paths'
import {
  useAuthenticatedAccount,
  usePermission,
  useSelectedTrial,
} from 'src/features/auth/context'
import { useActiveMembersByTrialHospitalUids } from 'src/features/member/api/getActiveTrialMembersByTrialHospitalUids'
import { Trial } from 'src/features/trial/types'
import { useMirohaToast } from 'src/lib/chakra-theme/components/toast/use-miroha-toast'
import { PERMISSIONS } from 'src/lib/permission'

import { useCreateExplanationSession } from '../../api'
import { ExplanationDetail } from '../../types'
import { willStartSession } from '../../utils/willStartSession'
import { MembersConfirmationModal } from '../MembersConfirmationModal/MembersConfirmationModal'
import { MembersSettingModal } from '../MembersSettingModal/MembersSettingModal'

type Props = {
  explanation: ExplanationDetail
  mutateExplanation: () => void
}

/** MiROHA画面側で説明を開始するボタンを含むコンポーネント
 *
 * 対面 -> 説明トップ画面へ遷移
 *
 * 遠隔/遠隔(パートナー) -> ロビーへ遷移
 *
 *  */
export const StartExplanationSession: React.FC<Props> = ({
  mutateExplanation,
  explanation,
}) => {
  const { hasPermission } = usePermission()

  const { selectedTrial } = useSelectedTrial()

  const { members, partnerTrialHospital } = explanation.latestRevision
  const { data: activeMembers } = useActiveMembersByTrialHospitalUids({
    shouldCancel: !explanation.trialHospital.uid && !partnerTrialHospital?.uid,
    trialHospitalUids: [
      explanation.trialHospital.uid,
      partnerTrialHospital?.uid ?? '',
    ].filter(Boolean),
  })
  const memberUids = members.map(m => m.trialMember.uid)
  const activeMemberUids = activeMembers?.map(m => m.uid) ?? []
  const hasDisabledMember = memberUids.every(uid =>
    activeMemberUids.includes(uid),
  )

  if (!hasPermission(PERMISSIONS.Explanation_Edit)) return null
  if (explanation.isRequiredReAgreement) return null

  const showButton =
    // 面談中(=最新の説明セッションが終了されていない)の場合は開始ボタンを表示
    (explanation.latestRevision.latestSessionUid &&
      !explanation.latestRevision.isLatestSessionFinished) ||
    // ステータスが説明セッション開始可能な場合は開始ボタンを表示
    willStartSession(explanation.latestRevision.status)

  if (!showButton) {
    return null
  }

  return explanation.latestRevision.type === 'InPerson' ? (
    <StartInPersonSession
      explanation={explanation}
      trialUid={selectedTrial.uid}
      mutateExplanation={mutateExplanation}
      hasDisabledMember={hasDisabledMember}
    />
  ) : (
    <StartRemoteOrRemotePartnerSession
      explanation={explanation}
      trialUid={selectedTrial.uid}
      mutateExplanation={mutateExplanation}
      hasDisabledMember={hasDisabledMember}
    />
  )
}

const StartInPersonSession: React.FC<
  Omit<Props, 'explanationReservationUid'> & {
    trialUid: Trial['uid']
    hasDisabledMember: boolean
  }
> = ({ explanation, mutateExplanation, trialUid, hasDisabledMember }) => {
  const navigate = useNavigate()

  const toast = useMirohaToast()

  const { isOpen, onClose, onOpen } = useDisclosure()

  const {
    isOpen: isOpenSettingModal,
    onOpen: onOpenSettingModal,
    onClose: onCloseSettingModal,
  } = useDisclosure()

  const { request: createSession } = useCreateExplanationSession({
    onSuccess: ({ explanationSessionUid: sessionUid }) => {
      navigate(
        generatePath(Paths.HospitalExplanationRoomTop, {
          trialUid,
          sessionUid,
        }),
      )
    },
    onError: error => {
      toast({
        status: 'error',
        title: error.message,
      })
    },
  })

  return (
    <>
      <Tooltip
        label="担当者が無効化されているため説明を開始できません"
        isDisabled={hasDisabledMember}
      >
        <Button onClick={onOpen} isDisabled={!hasDisabledMember}>
          {explanation.latestRevision.status === 'SessionNotStarted'
            ? '説明開始'
            : '説明再開'}
        </Button>
      </Tooltip>
      <MembersConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        explanation={explanation}
        type="Button"
        onClick={async () => {
          await createSession({
            type: 'inPerson',
            explanationRevisionUid: explanation.latestRevision.uid,
          })
        }}
        onOpenSettingModal={() => {
          onClose()
          onOpenSettingModal()
        }}
      />
      <MembersSettingModal
        key={explanation.latestRevision.members
          .map(m => m.trialMember.uid)
          .join()}
        isOpen={isOpenSettingModal}
        onClose={() => {
          onCloseSettingModal()
          onOpen()
        }}
        explanation={explanation}
        explanationSessionUid={undefined}
        mutate={mutateExplanation}
        defaultMembers={explanation.latestRevision.members.map(m => ({
          trialHospitalUid: m.trialHospital.uid,
          trialMemberUid: m.trialMember.uid,
          role: m.trialMember.role,
        }))}
      />
    </>
  )
}

const StartRemoteOrRemotePartnerSession: React.FC<
  Props & { trialUid: Trial['uid']; hasDisabledMember: boolean }
> = ({ explanation, mutateExplanation, trialUid, hasDisabledMember }) => {
  const {
    account: { selectedTrialHospitalUid },
  } = useAuthenticatedAccount()

  const { isOpen, onClose, onOpen } = useDisclosure()

  const isPartner =
    explanation.latestRevision.partnerTrialHospital?.uid ===
    selectedTrialHospitalUid

  const {
    isOpen: isOpenSettingModal,
    onOpen: onOpenSettingModal,
    onClose: onCloseSettingModal,
  } = useDisclosure()

  // 同意説明を開始できるのは医療機関ユーザーのみ
  if (!selectedTrialHospitalUid) {
    throw new Error('selectedTrialHospitalUid is not defined')
  }

  const withoutReservationPath = isPartner
    ? Paths.PartnerExplanationRoomLobbyWithoutReservation
    : Paths.HospitalExplanationRoomLobbyWithoutReservation

  return (
    <>
      <Tooltip
        label="担当者が無効化されているため説明ロビーへは移動できません"
        isDisabled={hasDisabledMember}
      >
        <Button onClick={onOpen} isDisabled={!hasDisabledMember}>
          説明ロビーへ
        </Button>
      </Tooltip>
      <MembersConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        explanation={explanation}
        type="ExternalLink"
        href={generatePath(withoutReservationPath, {
          trialUid,
          explanationRevisionUid: explanation.latestRevision.uid,
        })}
        onOpenSettingModal={() => {
          onClose()
          onOpenSettingModal()
        }}
      />
      <MembersSettingModal
        key={explanation.latestRevision.members
          .map(m => m.trialMember.uid)
          .join()}
        isOpen={isOpenSettingModal}
        onClose={() => {
          onCloseSettingModal()
          onOpen()
        }}
        explanation={explanation}
        explanationSessionUid={explanation.latestRevision.latestSessionUid}
        mutate={mutateExplanation}
        defaultMembers={explanation.latestRevision.members.map(m => ({
          trialHospitalUid: m.trialHospital.uid,
          trialMemberUid: m.trialMember.uid,
          role: m.trialMember.role,
        }))}
      />
    </>
  )
}
